import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Copy } from 'lucide-react';
import { api } from '../../api/client';
import type { ApiClientSummary } from '@pay/shared';

interface ApiKeyRow {
  id: string;
  key_id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<ApiClientSummary | null>(null);
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [label, setLabel] = useState('');

  function refresh() {
    api.get<ApiClientSummary[]>('/api/admin/clients').then((cs) => {
      setClient(cs.find((c) => c.id === id) ?? null);
    });
    api.get<ApiKeyRow[]>(`/api/admin/keys?api_client_id=${id}`).then(setKeys);
  }
  useEffect(refresh, [id]);

  async function createKey() {
    const r = await api.post<{ key_id: string; token: string }>('/api/admin/keys', { api_client_id: id, label });
    setNewToken(r.token);
    setLabel('');
    refresh();
  }

  async function revokeKey(keyId: string) {
    if (!confirm('Tilbakekall denne nøkkelen?')) return;
    await api.post(`/api/admin/keys/${keyId}/revoke`);
    refresh();
  }

  if (!client) return <div className="p-6">Laster …</div>;

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-semibold mb-1">{client.name}</h1>
      <div className="text-sm text-slate-500 mb-6">
        Eiere: {client.scopes.map((s) => s.owner_name).join(', ') || '—'}
      </div>

      {newToken && (
        <div className="bg-amber-50 border border-amber-300 rounded p-4 mb-5">
          <div className="font-medium text-amber-900 mb-2">Ny nøkkel — kopier nå, vises ikke igjen!</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white p-2 rounded text-sm font-mono break-all">{newToken}</code>
            <button onClick={() => navigator.clipboard.writeText(newToken)} className="bg-amber-500 hover:bg-amber-600 text-white p-2 rounded">
              <Copy size={16} />
            </button>
            <button onClick={() => setNewToken(null)} className="px-3 py-2 text-sm">OK</button>
          </div>
        </div>
      )}

      <section className="bg-white rounded-lg shadow border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">API-nøkler</h2>
        </div>
        <div className="flex gap-2 mb-5">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Etikett (f.eks. 'produksjon')"
            className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <button onClick={createKey} className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm font-medium">
            <Plus size={14} /> Generer
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left py-2">Key ID</th>
              <th className="text-left py-2">Etikett</th>
              <th className="text-left py-2">Opprettet</th>
              <th className="text-left py-2">Sist brukt</th>
              <th className="text-left py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-t border-slate-100">
                <td className="py-2 font-mono">{k.key_id}</td>
                <td className="py-2">{k.label ?? '—'}</td>
                <td className="py-2 text-slate-600">{new Date(k.created_at).toLocaleDateString('nb-NO')}</td>
                <td className="py-2 text-slate-600">{k.last_used_at ? new Date(k.last_used_at).toLocaleString('nb-NO') : '—'}</td>
                <td className="py-2">{k.revoked_at ? <span className="text-red-600">Tilbakekalt</span> : <span className="text-green-700">Aktiv</span>}</td>
                <td className="py-2 text-right">
                  {!k.revoked_at && (
                    <button onClick={() => revokeKey(k.id)} className="text-red-600 hover:text-red-800 text-sm">
                      Tilbakekall
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-slate-500">
                  Ingen nøkler ennå.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
